/**
 * Application theme.
 *
 * All colours, spacing, radii, font sizes that previously lived in inline
 * `style={{}}` blocks consolidate here. Adding a new token? The TypeScript
 * module augmentation in `styled.d.ts` picks it up automatically.
 */
export const theme = {
  colors: {
    // Canvas & surfaces
    background: '#ffffff',
    surface: '#f8f9fa',
    surfaceMuted: '#fafafa',
    surfaceWarning: '#fffaeb',
    panel: '#ffffff',
    border: '#d1d5db',
    borderSubtle: '#e5e7eb',
    borderMuted: '#e0e0e0',
    borderDashed: '#ccc',

    // Text
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    textInverse: '#ffffff',
    textAccentWarn: '#9a6700',

    // Brand / interaction
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    primaryFaded: 'rgba(37, 99, 235, 0.1)',

    // Status
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    dangerText: '#c0392b',
    dangerSurface: '#fdf0ef',
    dangerSurfaceStrong: '#fde6e8',
    dangerBorder: '#f1c0c0',
    dangerBorderStrong: '#721c24',
    info: '#3b82f6',

    // Job-status badge colours
    statusSucceeded: '#28A745',
    statusFailed: '#DC3545',
    statusSkipped: '#FD7E14',
    statusRunning: '#3D2F84',

    // Node accents (workflow editor)
    nodeFlowControl: '#fef3c7',
    nodePdf: '#dbeafe',
    nodeAi: '#ede9fe',
    nodeIntegration: '#dcfce7',
    nodeRouteAccent: '#b36b00',
    nodeRouteSurface: '#fff6e5',
    nodeForEachBorder: '#1d6fb8',
    nodeForEachSurface: '#e6f2fb',
    nodeForEachSurfaceFaded: 'rgba(230, 242, 251, 0.4)',
    nodeUnknownBorder: '#e6a817',
    nodeUnknownSurface: '#fffbf0',

    // Node selection / invalid-state shadows. Kept as full rgba literals
    // because they're shadow tints (alpha-blended onto the canvas), not
    // hex base colours that participate in palette consistency.
    nodeSelectedShadow: 'rgba(61, 47, 132, 0.25)',
    nodeInvalidShadowSelected: 'rgba(192, 57, 43, 0.25)',
    nodeInvalidShadow: 'rgba(192, 57, 43, 0.35)',

    // Run colours (step status)
    runRunning: '#fbbf24',
    runSucceeded: '#34d399',
    runFailed: '#f87171',
    runSkipped: '#9ca3af',

    // Hover / ghost-button affordances
    hoverNeutral: '#f0f0f0',
    contextMenuBackground: '#ffffff',
    contextMenuBorder: '#e0e0e0',
    contextMenuShadow: 'rgba(0, 0, 0, 0.15)',

    // Output / diagnostics surfaces
    outputSurface: '#f4f8ff',
    errorTargetSurface: '#fbe9e7',

    // Modal / overlay
    overlay: 'rgba(0, 0, 0, 0.45)',
    modalShadow: 'rgba(0, 0, 0, 0.25)',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
  },
  radii: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    pill: '999px',
  },
  fontSizes: {
    xs: '11px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '22px',
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    // Slight blue-tinted hover shadow — matches the legacy `--shadow-md`
    // value in styles.css. Kept distinct from `md` so the palette tile
    // hover state preserves its existing visual.
    paletteHover: '0 4px 12px rgba(20, 15, 60, 0.08)',
  },
  zIndex: {
    base: 1,
    sticky: 100,
    dropdown: 200,
    modal: 1000,
    tooltip: 1100,
  },
} as const;

export type AppTheme = typeof theme;
